import set from 'lodash.set';
import MessageRenderer, {
  TransactionalCodeSnippetLanguage,
  TransactionalCodeSnippetMessage,
} from 'cio/utils/transactional/code-snippet-request-renderer';
import { IdentifierType } from 'cio/utils/identifiers';
import { DataCenter } from 'cio/models/account';
import { getApiHost } from 'cio/utils/data-centers';
import { TransactionalMessageChannelType } from 'cio/models/transactional-message';
import { isEmpty } from '@ember/utils';
import { Editor } from 'cio/services/editor';

function variable(content: string) {
  return `____${content}____`;
}

export function replaceSnippetVariableWithHTMLTag(content: string) {
  return content.replace(
    /____(.*)____/g,
    (_, match) => `<span class="code-snippet__variable" data-content="${match}"></span>`,
  );
}

export const setViaAPI = variable('Set in your API call');
export const insertAppApiKey = variable('Insert your App API Key');
export const recipientEmail = variable('Recipient email');
export const senderEmail = variable('Sender email');
export const customerId = variable('Customer id');
export const customerEmail = variable('Customer email');

export type TransactionalCodeSnippetEmailVars = {
  from?: string;
  subject?: string;
  body?: string;
};

export const pushTitle = variable('Push title');
export const pushMessage = variable('Push message');
export const pushCustomPayload = variable('Custom payload');

export type TransactionalCodeSnippetPushVars = {
  title?: string;
  message?: string;
  custom_payload_empty?: boolean;
};

export type TransactionalCodeSnippetVars = TransactionalCodeSnippetEmailVars &
  TransactionalCodeSnippetPushVars & {
    editor: Editor;
    transactionalMessageId: string;
    messageVariables: string[];
    context: {
      dataCenter: DataCenter;
    };
  };

// Given an array like:
//
// ["trigger.name", "trigger.person.name", "trigger.items[0].name", "customer.email"]
//
// Returns an object like:
//
// {
//    name: <setViaApi>,
//    person: {
//      name: <setViaApi>,
//    },
//    items: <setViaApi>,
// }
//
// This helps us traverse through the object recursively and generate code
// for the object in different languages later. It also ignores any customer.* variables
// as it's not overridable by the API.
export function generateContentVariablesObject(variables: string[]) {
  let message: TransactionalCodeSnippetMessage = {};
  variables
    .filter((item: string) => !item.startsWith('customer'))
    .map((item) => item.replace(/trigger\.?/, ''))
    .forEach((item) => {
      set(message, item, setViaAPI);
    });
  return message;
}

function generateRequest(
  vars: TransactionalCodeSnippetVars,
  identifierType: IdentifierType,
  channelType: TransactionalMessageChannelType,
) {
  let message: Record<string, string | number | TransactionalCodeSnippetMessage> = {
    transactional_message_id: vars.transactionalMessageId,
    message_data: generateContentVariablesObject(vars.messageVariables as string[]),
    identifiers: {
      id: customerId,
    },
  };

  if (identifierType === IdentifierType.Email) {
    message.identifiers = {
      email: customerEmail,
    };
  }

  switch (channelType) {
    case TransactionalMessageChannelType.Email:
      message.to = recipientEmail;

      // If from or subject isn't set already, the user has to provide them in the message.
      if (isEmpty(vars.from)) {
        message.from = senderEmail;
      }

      if (isEmpty(vars.subject)) {
        message.subject = setViaAPI;
      }

      if (isEmpty(vars.body)) {
        message.body = setViaAPI;
      }
      break;
    case TransactionalMessageChannelType.Push: {
      if (vars.editor === Editor.JSON) {
        if (vars.custom_payload_empty) {
          message.custom_payload = pushCustomPayload;
        }
      } else {
        if (isEmpty(vars.title)) {
          message.title = pushTitle;
        }

        if (isEmpty(vars.message)) {
          message.message = pushMessage;
        }
      }

      break;
    }
  }

  return message;
}

function renderRequest(
  vars: TransactionalCodeSnippetVars,
  language: TransactionalCodeSnippetLanguage,
  identifierType: IdentifierType,
  channelType: TransactionalMessageChannelType,
) {
  let renderer = new MessageRenderer();
  return renderer.render(generateRequest(vars, identifierType, channelType), language, channelType);
}

export type TransactionalCodeSnippet = {
  name: TransactionalCodeSnippetLanguage;
  language: string;
  content: (vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) => string;
};

const emailSnippets: Readonly<TransactionalCodeSnippet[]> = Object.freeze([
  {
    name: 'Node.js',
    language: 'javascript',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `const { APIClient, SendEmailRequest } = require("customerio-node");

const client = new APIClient("${insertAppApiKey}");

const request = ${renderRequest(vars, 'Node.js', identifierType, TransactionalMessageChannelType.Email)}

client.sendEmail(request)
  .then(res => console.log(res))
  .catch(err => console.log(err.statusCode, err.message))`;
    },
  },
  {
    name: 'Python',
    language: 'python',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `from customerio import APIClient, SendEmailRequest, CustomerIOException

api = APIClient("${insertAppApiKey}")

request = ${renderRequest(vars, 'Python', identifierType, TransactionalMessageChannelType.Email)}

try:
  api.send_email(request)
except CustomerIOException as e:
  print("error: ", e)`;
    },
  },
  {
    name: 'Go',
    language: 'go',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `import "github.com/customerio/go-customerio"

client := customerio.NewAPIClient("${insertAppApiKey}");

request := ${renderRequest(vars, 'Go', identifierType, TransactionalMessageChannelType.Email)}

body, err := client.SendEmail(context.Background(), &request)
if err != nil {
  fmt.Println(err)
}

fmt.Println(body)`;
    },
  },
  {
    name: 'Ruby',
    language: 'ruby',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `require "customerio"

client = Customerio::APIClient.new("${insertAppApiKey}")

request = ${renderRequest(vars, 'Ruby', identifierType, TransactionalMessageChannelType.Email)}

begin
  response = client.send_email(request)
  puts response
rescue Customerio::InvalidResponse => e
  puts e.message, e.code, e.response
end`;
    },
  },
  {
    name: 'cURL',
    language: 'bash',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `request='
${renderRequest(vars, 'cURL', identifierType, TransactionalMessageChannelType.Email)}
'

echo $request | curl -v https://${getApiHost('api', vars.context.dataCenter)}.customer.io/v1/send/email \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer ${insertAppApiKey}' \\
-d @-`;
    },
  },
]);

const pushSnippets: Readonly<TransactionalCodeSnippet[]> = Object.freeze([
  {
    name: 'Node.js',
    language: 'javascript',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `const { APIClient, SendPushRequest } = require("customerio-node");

const client = new APIClient("${insertAppApiKey}");

const request = ${renderRequest(vars, 'Node.js', identifierType, TransactionalMessageChannelType.Push)}

client.sendPush(request)
  .then(res => console.log(res))
  .catch(err => console.log(err.statusCode, err.message))`;
    },
  },
  {
    name: 'Python',
    language: 'python',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `from customerio import APIClient, SendPushRequest, CustomerIOException

api = APIClient("${insertAppApiKey}")

request = ${renderRequest(vars, 'Python', identifierType, TransactionalMessageChannelType.Push)}

try:
  api.send_push(request)
except CustomerIOException as e:
  print("error: ", e)`;
    },
  },
  {
    name: 'Go',
    language: 'go',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `import "github.com/customerio/go-customerio"

client := customerio.NewAPIClient("${insertAppApiKey}");

request := ${renderRequest(vars, 'Go', identifierType, TransactionalMessageChannelType.Push)}

body, err := client.SendPush(context.Background(), &request)
if err != nil {
  fmt.Println(err)
}

fmt.Println(body)`;
    },
  },
  {
    name: 'Ruby',
    language: 'ruby',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `require "customerio"

client = Customerio::APIClient.new("${insertAppApiKey}")

request = ${renderRequest(vars, 'Ruby', identifierType, TransactionalMessageChannelType.Push)}

begin
  response = client.send_push(request)
  puts response
rescue Customerio::InvalidResponse => e
  puts e.message, e.code, e.response
end`;
    },
  },
  {
    name: 'cURL',
    language: 'bash',
    content(vars: TransactionalCodeSnippetVars, identifierType: IdentifierType) {
      return `request='
${renderRequest(vars, 'cURL', identifierType, TransactionalMessageChannelType.Push)}
'

echo $request | curl -v https://${getApiHost('api', vars.context.dataCenter)}.customer.io/v1/send/push \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer ${insertAppApiKey}' \\
-d @-`;
    },
  },
]);

const codeSnippets = {
  [TransactionalMessageChannelType.Email]: emailSnippets,
  [TransactionalMessageChannelType.Push]: pushSnippets,
};

export default codeSnippets;
